import React, { useState } from "react";
import { DownloadSimple, X, DeviceMobile, DotsThreeVertical } from "@phosphor-icons/react";
import {
    useInstallState,
    promptInstall,
} from "@/lib/pwaInstall";

const DISMISSED_KEY = "nd:install:dismissed";
const DISMISS_DAYS = 7;

function dismissedRecently() {
    if (typeof window === "undefined") return false;
    const at = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    return at && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Should we surface any install UI at all?  V1.9.14:
 *   • Already installed / running standalone → never.
 *   • Chrome captured `beforeinstallprompt` → yes.
 *   • Any mobile (iOS Safari OR Android Chrome) → yes.
 *   • Chromium desktop (Chrome / Edge / Brave / Opera on desktop) → yes,
 *     fall back to the desktop instructions modal so the CTA never
 *     silently disappears if `beforeinstallprompt` hasn't fired yet.
 *   • Firefox desktop → hidden (Firefox has no PWA install support).
 */
function isChromiumDesktop() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent;
    if (/android|iphone|ipad|ipod/i.test(ua)) return false; // mobile handled elsewhere
    return /Chrome|Chromium|Edg|OPR|Brave/i.test(ua);
}

export function shouldShowInstall({ canInstall, isMobile, isStandalone }) {
    if (isStandalone) return false;
    return canInstall || isMobile || isChromiumDesktop();
}

export default function InstallPrompt() {
    const state = useInstallState();
    const [dismissed, setDismissed] = useState(dismissedRecently());
    const [helpOpen, setHelpOpen] = useState(false);

    // V2.1.1 — Suppress on paid-ad landings. The install nag over the
    // primary WhatsApp CTA is the #1 CVR killer we see in early A/B.
    if (typeof window !== "undefined") {
        const p = window.location.pathname || "";
        if (p.startsWith("/quick-apply") || p.startsWith("/apply/")) return null;
    }

    if (!shouldShowInstall(state)) return null;
    if (dismissed) return null;

    const install = async () => {
        if (state.canInstall) {
            const ok = await promptInstall();
            if (!ok) setHelpOpen(true); // user dismissed the browser prompt
        } else {
            setHelpOpen(true);
        }
    };

    const dismiss = () => {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        setDismissed(true);
    };

    return (
        <>
            <div
                data-testid="install-prompt"
                className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-40 bg-ink text-white border border-ink shadow-lg p-4 flex items-start gap-3"
            >
                <div className="w-9 h-9 bg-cobalt flex items-center justify-center shrink-0">
                    <DeviceMobile size={18} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-black uppercase tracking-wider">
                        Install NaukriDaddy
                    </p>
                    <p className="text-xs text-white/80 mt-1 leading-snug">
                        Add to your home screen for faster access and offline browsing.
                    </p>
                    <div className="flex gap-2 mt-3">
                        <button
                            onClick={install}
                            data-testid="install-button"
                            className="bg-cobalt hover:bg-cobalt-dark text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                        >
                            <DownloadSimple size={12} weight="bold" /> Install
                        </button>
                        <button
                            onClick={dismiss}
                            data-testid="install-dismiss"
                            className="text-white/70 hover:text-white px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider"
                        >
                            Not now
                        </button>
                    </div>
                </div>
                <button
                    onClick={dismiss}
                    className="text-white/60 hover:text-white"
                    aria-label="Dismiss"
                >
                    <X size={14} weight="bold" />
                </button>
            </div>

            <InstallHelp
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                state={state}
            />
        </>
    );
}

/**
 * Standalone button that any menu / footer can drop in.  Uses the same
 * `shouldShowInstall` gate as the floater, so it's always in sync.
 */
export function InstallAppButton({
    className = "",
    label = "Install app",
    testId = "install-app-button",
}) {
    const state = useInstallState();
    const [helpOpen, setHelpOpen] = useState(false);

    if (!shouldShowInstall(state)) return null;

    const tap = async () => {
        if (state.canInstall) {
            const ok = await promptInstall();
            if (!ok) setHelpOpen(true);
        } else {
            setHelpOpen(true);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={tap}
                data-testid={testId}
                className={
                    className ||
                    "inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/20 hover:border-cobalt hover:text-cobalt text-[11px] font-black uppercase tracking-wider transition rounded-md"
                }
            >
                <DownloadSimple size={12} weight="bold" /> {label}
            </button>
            <InstallHelp
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                state={state}
            />
        </>
    );
}

/**
 * Instructions modal — routes to the correct platform-specific
 * step-by-step for adding NaukriDaddy to the home screen.
 */
function InstallHelp({ open, onClose, state }) {
    if (!open) return null;
    const isIOS = state?.isIOS;
    const isAndroid = state?.isAndroid;
    const heading = isIOS
        ? "Install on iPhone / iPad"
        : isAndroid
            ? "Install on Android"
            : "Install NaukriDaddy";
    return (
        <div
            className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={onClose}
            data-testid="install-help"
        >
            <div
                className="bg-white p-5 max-w-sm w-full border border-[#E5E5E5] rounded-t-2xl sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                    Add to Home Screen
                </p>
                <h3 className="font-heading text-lg font-black mb-3">{heading}</h3>
                {isIOS ? (
                    <ol className="text-sm space-y-2 text-[#525252]">
                        <li>1. In Safari, tap the <b>Share</b> icon at the bottom.</li>
                        <li>2. Scroll down and tap <b>Add to Home Screen</b>.</li>
                        <li>3. Tap <b>Add</b> in the top-right.</li>
                    </ol>
                ) : isAndroid ? (
                    <ol className="text-sm space-y-2 text-[#525252]">
                        <li>
                            1. Tap the <DotsThreeVertical size={14} weight="bold" className="inline align-middle text-ink" /> menu at the top-right of Chrome.
                        </li>
                        <li>
                            2. Tap <b>Install app</b> (or <b>Add to Home Screen</b>).
                        </li>
                        <li>3. Tap <b>Install</b> in the popup that appears.</li>
                    </ol>
                ) : (
                    <ol className="text-sm space-y-2 text-[#525252]">
                        <li>1. Open <b>NaukriDaddy</b> in Google Chrome or Microsoft Edge.</li>
                        <li>
                            2. Click the install icon in the address bar (looks like a monitor with a down-arrow) or open the browser menu → <b>Install NaukriDaddy</b>.
                        </li>
                        <li>3. Confirm <b>Install</b>.</li>
                    </ol>
                )}
                <button
                    onClick={onClose}
                    data-testid="install-help-close"
                    className="mt-5 bg-ink text-white px-4 py-2 text-xs font-bold uppercase tracking-wider w-full sm:w-auto"
                >
                    Got it
                </button>
            </div>
        </div>
    );
}
